// Centro Médico → Catálogos → Canales de notificación.
// Configura Email (SMTP) y WhatsApp (Twilio) por empresa para el envío real de
// recordatorios de cita y el relay de notificaciones a pacientes. Credenciales
// write-only (se guardan cifradas). El envío lo ejecuta el worker ../notificador/.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, Loader2, Save, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, Switch, fieldCls, medRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Config {
  email_activo: boolean; smtp_host: string | null; smtp_port: number; smtp_secure: boolean;
  smtp_user: string | null; email_from: string | null; email_from_nombre: string | null;
  smtp_pass_configurada: boolean;
  whatsapp_activo: boolean; twilio_sid: string | null; twilio_whatsapp_from: string | null;
  twilio_token_configurada: boolean; relay_notificaciones: boolean;
}
interface EnvioLog {
  origen: string; id: string; canal: string; destino: string; estado: string;
  programado_para: string; enviado_at: string | null; error: string | null;
}

const ESTADO_CLS: Record<string, string> = {
  enviado: 'text-completado', fallido: 'text-urgente', pendiente: 'text-pendiente',
  enviando: 'text-estable', cancelado: 'text-muted-foreground',
};

export function Canales() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Config>({ queryKey: ['med-canales'], queryFn: () => medRpc<Config>('med_canales_get') });
  const { data: log = [] } = useQuery<EnvioLog[]>({ queryKey: ['med-envios'], queryFn: () => medRpc<EnvioLog[]>('med_envios', { _limite: 50 }), refetchInterval: 20000 });

  const [f, setF] = useState<Config | null>(null);
  const [smtpPass, setSmtpPass] = useState('');
  const [twToken, setTwToken] = useState('');
  useEffect(() => { if (data) setF(data); }, [data]);
  const set = (k: keyof Config, v: unknown) => setF((p) => (p ? { ...p, [k]: v } : p));

  const guardar = useMutation({
    mutationFn: async () => {
      if (!f) return;
      const _data: Record<string, unknown> = {
        email_activo: f.email_activo, smtp_host: f.smtp_host, smtp_port: f.smtp_port,
        smtp_secure: f.smtp_secure, smtp_user: f.smtp_user, email_from: f.email_from,
        email_from_nombre: f.email_from_nombre, whatsapp_activo: f.whatsapp_activo,
        twilio_sid: f.twilio_sid, twilio_whatsapp_from: f.twilio_whatsapp_from,
        relay_notificaciones: f.relay_notificaciones,
      };
      if (smtpPass) _data.smtp_pass = smtpPass;
      if (twToken) _data.twilio_token = twToken;
      await medRpc('med_canales_set', { _data });
    },
    onSuccess: () => {
      toast.success('Canales actualizados');
      setSmtpPass(''); setTwToken('');
      void qc.invalidateQueries({ queryKey: ['med-canales'] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error al guardar'),
  });

  if (isLoading || !f) return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-3xl space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Send className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Canales de notificación</h1>
          <p className="text-sm text-muted-foreground">Envío real de recordatorios de cita y notificaciones a pacientes.</p>
        </div>
      </div>

      {/* Email */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email (SMTP)</span>
            <Switch checked={f.email_activo} onChange={(v) => set('email_activo', v)} label="Activo" />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Campo label="Servidor SMTP"><input className={fieldCls} placeholder="smtp.gmail.com" value={f.smtp_host ?? ''} onChange={(e) => set('smtp_host', e.target.value)} /></Campo>
          <Campo label="Puerto"><input type="number" className={fieldCls} value={f.smtp_port ?? 587} onChange={(e) => set('smtp_port', Number(e.target.value))} /></Campo>
          <Campo label="Usuario"><input className={fieldCls} autoComplete="off" value={f.smtp_user ?? ''} onChange={(e) => set('smtp_user', e.target.value)} /></Campo>
          <Campo label={`Contraseña ${f.smtp_pass_configurada ? '(configurada)' : ''}`}>
            <input type="password" autoComplete="new-password" className={fieldCls} placeholder={f.smtp_pass_configurada ? 'En blanco = no cambiar' : ''} value={smtpPass} onChange={(e) => setSmtpPass(e.target.value)} />
          </Campo>
          <Campo label="Remitente (email)"><input className={fieldCls} placeholder="clinica@dominio.com" value={f.email_from ?? ''} onChange={(e) => set('email_from', e.target.value)} /></Campo>
          <Campo label="Remitente (nombre)"><input className={fieldCls} placeholder="Centro Médico" value={f.email_from_nombre ?? ''} onChange={(e) => set('email_from_nombre', e.target.value)} /></Campo>
          <div className="sm:col-span-2"><Switch checked={f.smtp_secure} onChange={(v) => set('smtp_secure', v)} label="Conexión segura directa (TLS/465). Desactivado = STARTTLS (587)." /></div>
        </CardContent>
      </Card>

      {/* WhatsApp */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2"><MessageCircle className="h-4 w-4" /> WhatsApp (Twilio)</span>
            <Switch checked={f.whatsapp_activo} onChange={(v) => set('whatsapp_activo', v)} label="Activo" />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Campo label="Account SID"><input className={fieldCls} autoComplete="off" value={f.twilio_sid ?? ''} onChange={(e) => set('twilio_sid', e.target.value)} /></Campo>
          <Campo label={`Auth Token ${f.twilio_token_configurada ? '(configurado)' : ''}`}>
            <input type="password" autoComplete="new-password" className={fieldCls} placeholder={f.twilio_token_configurada ? 'En blanco = no cambiar' : ''} value={twToken} onChange={(e) => setTwToken(e.target.value)} />
          </Campo>
          <Campo label="Número WhatsApp (from)" className="sm:col-span-2"><input className={fieldCls} placeholder="whatsapp:+14155238886" value={f.twilio_whatsapp_from ?? ''} onChange={(e) => set('twilio_whatsapp_from', e.target.value)} /></Campo>
          <p className="sm:col-span-2 text-[11px] text-muted-foreground">Usa el número del sandbox de Twilio o uno aprobado. Los teléfonos de los pacientes deben estar en formato internacional (+593…).</p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center justify-between gap-4 p-4">
          <Switch checked={f.relay_notificaciones} onChange={(v) => set('relay_notificaciones', v)}
            label="Enviar también las notificaciones a pacientes (cita/resultados/receta) por estos canales" />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>
          {!guardar.isPending && <Save className="h-4 w-4" />} Guardar configuración
        </Button>
      </div>

      {/* Log de envíos */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Actividad reciente</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Programado</th><th className="py-2 pr-3">Canal</th><th className="py-2 pr-3">Destino</th>
                <th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Enviado</th><th className="py-2 pr-3">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {log.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin envíos todavía.</td></tr>}
              {log.map((e) => (
                <tr key={`${e.origen}-${e.id}`} className="border-b last:border-0">
                  <td className="py-1.5 pr-3 whitespace-nowrap text-xs">{new Date(e.programado_para).toLocaleString(LOCALE)}</td>
                  <td className="py-1.5 pr-3">{e.canal}</td>
                  <td className="py-1.5 pr-3"><span className="inline-block max-w-52 truncate align-middle" title={e.destino}>{e.destino}</span></td>
                  <td className={`py-1.5 pr-3 font-medium ${ESTADO_CLS[e.estado] || ''}`}>{e.estado}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-xs">{e.enviado_at ? new Date(e.enviado_at).toLocaleTimeString(LOCALE) : '—'}</td>
                  <td className="py-1.5 pr-3 text-xs text-muted-foreground"><span className="inline-block max-w-72 truncate align-middle" title={e.error || ''}>{e.error || '—'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
